import { type ComponentPropsWithoutRef } from 'react';
import Modal from '@/components/Modal';
import { BlockButton } from '@/components/BlockButton';
import clsx from 'clsx';
import usePostCandidateEndpointBulkProgress from '../api/usePostCandidateEndpointBulkProgress';
import usePostCandidateEndpointBulkWithdraw from '../api/usePostCandidateEndpointBulkWithdraw';
import usePostCandidateEndpointBulkReset from '../api/usePostCandidateEndpointBulkReset';
import usePostCandidateEndpointBulkDestroy from '../api/usePostCandidateEndpointBulkDestroy';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import type { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import type { CandidateEndpointResponse } from '../types/CandidateEndpoint';

export type StatusChangeType = 'toSuggest' | 'toSelection' | 'toWithdraw' | 'toDelete';

type Props = {
  modal: Omit<ComponentPropsWithoutRef<typeof Modal>, 'children'>;
  close: () => void;
  statusChangeType: StatusChangeType;
  candidateProfileId: number;
  ids: number[];
  candidateEndpointRefetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<CandidateEndpointResponse, Error>>;
};

export default function CandidatesStatusChangeModal({
  modal,
  close,
  statusChangeType,
  candidateProfileId,
  ids,
  candidateEndpointRefetch,
}: Props) {
  const { mutateAsync: mutateProgress, isPending: isPendingProgress } =
    usePostCandidateEndpointBulkProgress();
  const { mutateAsync: mutateWithdraw, isPending: isPendingWithdraw } =
    usePostCandidateEndpointBulkWithdraw();
  const { mutateAsync: mutateReset, isPending: isPendingReset } =
    usePostCandidateEndpointBulkReset();
  const { mutateAsync: mutateDestroy, isPending: isPendingDestroy } =
    usePostCandidateEndpointBulkDestroy();

  const isSubmitting = isPendingProgress || isPendingWithdraw || isPendingReset || isPendingDestroy;

  const handleStatusChange = async () => {
    if (isSubmitting) return;
    try {
      if (statusChangeType === 'toWithdraw') {
        await mutateWithdraw({ candidateProfileId, ids });
      } else if (statusChangeType === 'toSelection') {
        await mutateProgress({ candidateProfileId, ids });
      } else if (statusChangeType === 'toSuggest') {
        await mutateReset({ candidateProfileId, ids });
      } else if (statusChangeType === 'toDelete') {
        await mutateDestroy({ candidateProfileId, ids });
      }
      await candidateEndpointRefetch();

      showSuccessToast(
        statusChangeType === 'toDelete' ? '削除が完了しました' : '変更が完了しました'
      );
      close();
    } catch (error) {
      showErrorToast(
        statusChangeType === 'toDelete' ? '削除に失敗しました' : 'ステータス変更に失敗しました'
      );
    }
  };
  const getModalConfig = () => {
    switch (statusChangeType) {
      case 'toSuggest':
        return {
          title: '変更の確認',
          confirmText: '提案へ戻す',
          confirmButtonWidth: 'w-[94px]',
          message: 'ステータスを「提案中」に変更しますか？',
        };
      case 'toSelection':
        return {
          title: '変更の確認',
          confirmText: '選考へ進む',
          confirmButtonWidth: 'w-[94px]',
          message: 'ステータスを「選考」に変更しますか？',
        };
      case 'toWithdraw':
        return {
          title: '変更の確認',
          confirmText: '取り下げ',
          confirmButtonWidth: 'w-[80px]',
          message: 'ステータスを「取り下げ」に変更しますか？',
        };
      case 'toDelete':
        return {
          title: '削除の確認',
          confirmText: '削除',
          confirmButtonWidth: 'w-[80px]',
          message: 'この提案求人を完全に削除します。この操作は元に戻せません。',
        };
    }
  };

  const { title, confirmText, confirmButtonWidth, message } = getModalConfig();

  return (
    <Modal {...modal}>
      <div className="w-[640px] rounded bg-white">
        <div className="flex h-[56px] items-center border-b border-solid border-border px-6">
          <p className="text-base font-bold">{title}</p>
        </div>
        <div className="p-6">
          <p className="text-base">{message}</p>
        </div>
        <div className="flex h-[74px] items-center justify-end gap-4 border-t border-solid border-border px-6">
          <div className="w-[94px]">
            <BlockButton type="button" text="キャンセル" colorType="white" onClick={close} />
          </div>
          <div className={clsx(confirmButtonWidth)}>
            <BlockButton
              type="button"
              disabled={isSubmitting}
              text={isSubmitting ? '送信中…' : confirmText}
              colorType={
                statusChangeType === 'toWithdraw' || statusChangeType === 'toDelete'
                  ? 'red'
                  : 'blue'
              }
              onClick={handleStatusChange}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
